import { AIS_CONFIG } from '@/utils/build-config';
import { elBackAxios } from '@/services/myAxios';

const shipDataCache = new Map();

function sanitizeImo(imo) {
  if (imo === null || imo === undefined) {
    return null;
  }

  const match = String(imo).match(/\d+/g);
  if (!match) {
    return null;
  }

  return match.join('');
}

function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function toIsoOrNull(value) {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function getFrontendBaseUrl() {
  const configured = AIS_CONFIG?.FRONT_STATIC_BASE_URL;
  if (configured) {
    const trimmed = configured.replace(/\/$/, '');
    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed;
    }
    return `https://${trimmed}`;
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/\/$/, '');
  }

  return '';
}

function buildAbsoluteUrl(path) {
  if (!path) {
    return null;
  }

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const base = getFrontendBaseUrl();
  if (!base) {
    return null;
  }

  const normalizedPath = String(path).replace(/^\/+/, '');
  return `${base}/${normalizedPath}`;
}

function normalizeManagement(detail = {}) {
  return {
    pi: detail['P&I'] || detail.pi || '-',
    ism: detail.ism || '',
    ism_email: detail.ismEmail || detail.ism_email || '',
    ism_website: detail.ismWebsite || detail.ismWeb || detail.ism_website || '',
    ism_address: detail.ismAddress || '',
    manager: detail.manager || '',
    manager_email: detail.managerEmail || '',
    manager_website: detail.managerWebsite || '',
    manager_address: detail.managerAddress || '',
    registered_owner: detail.registeredOwner || '',
    registered_owner_email: detail.registeredOwnerEmail || '',
    registered_owner_website: detail.registeredOwnerWebsite || '',
    registered_owner_address: detail.registeredOwnerAddress || '',
    classification_society: detail.ClassificationSociety || detail.classificationSociety || ''
  };
}

function normalizeEngine(detail = {}) {
  if (!detail || Object.keys(detail).length === 0) {
    return [];
  }

  return [
    {
      fuel_type: detail.fuelType || '-',
      propeller: detail.Propeller || detail.propeller || '-',
      engine_type: detail.engineType || '-',
      engine_builder: detail.engineBuilder || '-',
      engine_power_kw: detail['enginePower(kW)'] || detail.enginePower || ''
    }
  ];
}

function normalizePorts(portsPayload) {
  if (!portsPayload) {
    return [];
  }

  if (Array.isArray(portsPayload)) {
    return portsPayload;
  }

  if (portsPayload.detail && Array.isArray(portsPayload.detail.ports)) {
    return portsPayload.detail.ports;
  }

  if (portsPayload.ports && Array.isArray(portsPayload.ports)) {
    return portsPayload.ports;
  }

  return [];
}

function normalizeAisResponse(response) {
  if (!response || typeof response !== 'object') {
    return null;
  }

  const locationDetail = response.location?.detail || {};
  const particulars = response.details?.particulars?.detail || {};
  const engineDetail = response.details?.engine?.detail?.engine || {};
  const managementDetail = response.details?.management?.detail?.management || {};

  const imoNumeric = sanitizeImo(
    response.imo || particulars.imo || locationDetail.imo
  );

  const latitude = toNumber(locationDetail.latitude);
  const longitude = toNumber(locationDetail.longitude);
  const speed = toNumber(locationDetail.speed);
  const course = toNumber(locationDetail.course);
  const timestamp = locationDetail.updateTime || locationDetail.positionReceived || null;

  const normalized = {
    raw: response,
    imo: imoNumeric ? `IMO${imoNumeric}` : null,
    imoNumeric,
    name: particulars.name || locationDetail.name || '',
    mmsi: particulars.mmsi || locationDetail.mmsi || response.mmsi || null,
    callsign: particulars.callsign || locationDetail.callsign || response.callsign || null,
    vessel_type: particulars.shipType || '',
    type_specific: particulars.typeSpecific || '',
    flag: particulars.country || '',
    country_iso: particulars.countryIso || '',
    build_year: particulars.yearOfBuilt || '',
    length: particulars.length || '',
    beam: particulars.beam || '',
    gross_tonnage: particulars.grossTonnage || '',
    deadweight: particulars.deadweight || '',
  teu: particulars.teu || '',
  eni: particulars.eni || '',
  bale: particulars.bale || '',
  hull: particulars.hull || '',
  ballastWater: particulars.ballastWater || '',
  freshWater: particulars.freshWater || '',
  crudeOil: particulars.crudeOil || '',
  gas: particulars.gas || '',
  grain: particulars.grain || '',
  builder: particulars.builder || '',
  material: particulars.material || '',
  placeOfBuild: particulars.placeOfBuild || '',
    draught: locationDetail.draught || '',
    current_draught: particulars.currentDraught || '',
    destination: locationDetail.destination || '',
    last_port: locationDetail.lastPort || '',
    eta_utc: locationDetail.etaUtc || '',
    eta_iso: toIsoOrNull(locationDetail.etaUtc),
    atd_utc: locationDetail.atdUtc || '',
    position_received: locationDetail.positionReceived || '',
    position_received_iso: toIsoOrNull(locationDetail.positionReceived),
    update_time: locationDetail.updateTime || '',
    update_time_iso: toIsoOrNull(locationDetail.updateTime),
    navigational_status: locationDetail.navigationalStatus || '',
    unlocode_destination: locationDetail.unlocode_destination || null,
    unlocode_lastport: locationDetail.unlocode_lastport || null,
  // location_updated: response.location_updated || '',
  // details_updated: response.details_updated || '',
  // ports_updated: response.ports_updated || '',
  // active: response.active ?? null,
    position: latitude !== null && longitude !== null
      ? {
          latitude,
          longitude,
          speed,
          course,
          timestamp
        }
      : null,
    ports: normalizePorts(response.ports),
    engine: normalizeEngine(engineDetail),
    management: normalizeManagement(managementDetail),
    image_url: response.details?.image_url || null,
    // image_local: response.details?.image_local || null,
    image_full_url:
      buildAbsoluteUrl(response.details?.image_local) || response.details?.image_url || null,
    weather: response.weather || null
  };

  return normalized;
}

export async function fetchShipAisData(imo, options = {}) {
  if (!AIS_CONFIG?.USE_REMOTE_AIS_SERVICE) {
    return null;
  }

  const { forceRefresh = false } = options;
  const imoNumeric = sanitizeImo(imo);

  if (!imoNumeric) {
    return null;
  }

  if (!forceRefresh && shipDataCache.has(imoNumeric)) {
    return shipDataCache.get(imoNumeric);
  }

  try {
    // Use elBackAxios instead of direct fetch
    const response = await elBackAxios.get('api/ais/position', {
      params: { imo: imoNumeric }
    });

    const payload = response.data;
    const normalized = normalizeAisResponse(payload);

    if (normalized) {
      shipDataCache.set(imoNumeric, normalized);
    }

    return normalized;
  } catch (error) {
    console.error(`Failed to fetch AIS data for IMO ${imoNumeric}:`, error);
    throw new Error(`AIS service error for IMO ${imoNumeric}: ${error.message}`);
  }
}

export function resolveShipImageUrl({ image_full_url: full, image_url: direct, image_local: local } = {}) {
  return full || direct || buildAbsoluteUrl(local);
}

export { sanitizeImo };